import { useCallback, useMemo, useState } from 'react';
import { useWizard } from 'react-use-wizard';
import { Check } from 'lucide-react';

import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { cn } from '@client/lib';

import { ASSET_TYPES, type AssetTypeId } from '@/features/guided-financial-planning/config';
import {
  useSession,
  useSessionMutations,
  useIsSessionComplete,
} from '@/features/guided-financial-planning/context';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';

interface AssetCardProps {
  id: AssetTypeId;
  label: string;
  isSelected: boolean;
  onToggle: (id: AssetTypeId) => void;
  disabled?: boolean;
}

function AssetCard({ id, label, isSelected, onToggle, disabled }: AssetCardProps) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onToggle(id)}
      disabled={disabled}
      className={cn(
        'relative flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all duration-200',
        disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-primary/50 hover:bg-primary/5',
        isSelected ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white',
      )}
    >
      <div
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200',
          isSelected ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white',
        )}
      >
        {isSelected && <Check className="size-4" />}
      </div>
      <span className="font-medium text-gray-900">{label}</span>
    </button>
  );
}

export function AssetSelectionStep() {
  const { nextStep } = useWizard();
  const { session } = useSession();
  const { updateStepData, isSaving } = useSessionMutations();
  const isSessionComplete = useIsSessionComplete();
  const { flags } = useFeatureFlags();

  const sessionAssets = session?.stepData?.selectedAssets;
  const [localOverrides, setLocalOverrides] = useState<Set<AssetTypeId> | null>(null);

  const assetTypesToShow = useMemo(
    () =>
      flags.existingLtcPolicyTypeOnCard
        ? ASSET_TYPES
        : ASSET_TYPES.filter(a => a.id !== 'existing-ltc-policy'),
    [flags.existingLtcPolicyTypeOnCard],
  );

  const sessionAssetsSet = useMemo(
    () => new Set<AssetTypeId>(sessionAssets ?? []),
    [sessionAssets],
  );

  const selectedAssets = localOverrides ?? sessionAssetsSet;

  const handleToggle = useCallback(
    (id: AssetTypeId) => {
      const current = localOverrides ?? sessionAssetsSet;
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      setLocalOverrides(next);
    },
    [localOverrides, sessionAssetsSet],
  );

  const handleNext = useCallback(async () => {
    const selectedArray = Array.from(selectedAssets) as AssetTypeId[];
    await updateStepData({ selectedAssets: selectedArray });
    nextStep();
  }, [selectedAssets, updateStepData, nextStep]);

  const hasSelection = selectedAssets.size > 0;

  return (
    <StepLayout
      title="What assets do you have?"
      subtitle="Select all the accounts and assets you currently own. We'll use this to assess your long-term care coverage capacity."
      isNextDisabled={!hasSelection || isSaving}
      nextButtonLabel="Continue"
      onNext={handleNext}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {assetTypesToShow.map(asset => (
          <AssetCard
            key={asset.id}
            id={asset.id}
            label={asset.label}
            isSelected={selectedAssets.has(asset.id)}
            onToggle={handleToggle}
            disabled={isSessionComplete}
          />
        ))}
      </div>
    </StepLayout>
  );
}
